import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Gift, Heart, LogOut, ExternalLink, LogIn } from "lucide-react";
import { toast } from "sonner";
import { API } from "../App";
import { getCategoryIcon } from "../utils/categoryIcons";
import Footer from "../components/Footer";

const PRIORITY_LABELS = {
  1: { label: "Un petit kiff 😊", color: "bg-muted text-muted-foreground" },
  2: { label: "Ça me ferait plaisir 😄", color: "bg-accent text-accent-foreground" },
  3: { label: "J'en rêve 🔥", color: "bg-primary text-primary-foreground" }
};

export default function PublicWishlist() {
  const { username } = useParams();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [owner, setOwner] = useState(null);
  const [wishes, setWishes] = useState([]);
  const [filteredWishes, setFilteredWishes] = useState([]);
  const [activeFilter, setActiveFilter] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWishlist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  const fetchWishlist = async () => {
    try {
      // First, try to check if user is authenticated
      try {
        const authResponse = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setCurrentUser(authResponse.data);
        setIsAuthenticated(true);
        
        // If authenticated, use the authenticated endpoint for full data
        const wishesResponse = await axios.get(`${API}/wishes/user/${username}`, { withCredentials: true });
        setOwner(wishesResponse.data.owner);
        setWishes(wishesResponse.data.wishes);
        setFilteredWishes(wishesResponse.data.wishes);
      } catch (authError) {
        // Not authenticated - use public endpoint
        setIsAuthenticated(false);
        setCurrentUser(null);
        
        const publicResponse = await axios.get(`${API}/wishes/public/${username}`);
        setOwner(publicResponse.data.owner);
        setWishes(publicResponse.data.wishes);
        setFilteredWishes(publicResponse.data.wishes);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Cette liste n'existe pas");
    } finally {
      setLoading(false);
    }
  };

  const filterWishes = (priority) => {
    if (priority === activeFilter) {
      setFilteredWishes(wishes);
      setActiveFilter(null);
    } else {
      setFilteredWishes(wishes.filter(w => w.priority === priority));
      setActiveFilter(priority);
    }
  };

  const handleReserve = async (wishId) => {
    if (!isAuthenticated) {
      // Redirect to login with return URL
      navigate(`/login?redirect=/wishlist/${username}`);
      return;
    }
    
    try {
      await axios.post(`${API}/wishes/${wishId}/reserve`, {}, { withCredentials: true });
      toast.success("Cadeau réservé ! 🎁");
      fetchWishlist();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur de réservation");
    }
  };

  const handleUnreserve = async (wishId) => {
    try {
      await axios.delete(`${API}/wishes/${wishId}/unreserve`, { withCredentials: true });
      toast.success("Réservation annulée");
      fetchWishlist();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setIsAuthenticated(false);
      setCurrentUser(null);
      fetchWishlist(); // Refresh with public data
    } catch (error) {
      toast.error("Erreur de déconnexion");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!owner) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="shadow-card max-w-md mx-4">
          <CardContent className="py-12 text-center">
            <Gift className="w-16 h-16 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="font-heading text-xl font-bold mb-2">Liste introuvable</h3>
            <p className="text-muted-foreground mb-4">Cette liste de souhaits n'existe pas.</p>
            <Link to="/">
              <Button className="rounded-full">Retour à l'accueil</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-12 flex flex-col">
      {/* Header */}
      <header className="bg-card/80 backdrop-blur-sm border-b sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Gift className="w-8 h-8 text-primary" strokeWidth={1.5} />
            <h1 className="font-heading text-xl font-bold" data-testid="wishlist-owner">
              Liste de souhaits de {owner?.name || username}
            </h1>
          </div>
          <div className="flex gap-2">
            {isAuthenticated ? (
              <>
                {currentUser?.username === username && (
                  <Button 
                    data-testid="my-dashboard-btn"
                    onClick={() => navigate('/dashboard')} 
                    variant="outline" 
                    size="sm"
                    className="rounded-full"
                  >
                    Mes cadeaux
                  </Button>
                )}
                <Button 
                  data-testid="logout-btn"
                  onClick={handleLogout} 
                  variant="ghost" 
                  size="sm"
                  className="rounded-full"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <Button 
                data-testid="login-btn"
                onClick={() => navigate(`/login?redirect=/wishlist/${username}`)} 
                variant="outline" 
                size="sm"
                className="rounded-full"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Se connecter
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8 flex-1">
        <div className="mb-8 text-center">
          <p className="text-2xl md:text-3xl font-normal text-muted-foreground">
            Offre-lui un truc qu'il veut VRAIMENT pour une fois 😏
          </p>
          {!isAuthenticated && (
            <p className="text-sm text-muted-foreground mt-2">
              Connecte-toi pour réserver un cadeau 🎁
            </p>
          )}
        </div>

        {/* Filtres Kiffomètre */}
        <div className="flex flex-wrap gap-2 justify-center mb-8" data-testid="priority-filters">
          <Button
            data-testid="filter-priority-3"
            onClick={() => filterWishes(3)}
            variant={activeFilter === 3 ? "default" : "outline"}
            className="rounded-full"
          >
            🔥 J'en rêve même la nuit
          </Button>
          <Button
            data-testid="filter-priority-2"
            onClick={() => filterWishes(2)}
            variant={activeFilter === 2 ? "default" : "outline"}
            className="rounded-full"
          >
            😄 Ça me ferait plaisir
          </Button>
          <Button
            data-testid="filter-priority-1"
            onClick={() => filterWishes(1)}
            variant={activeFilter === 1 ? "default" : "outline"}
            className="rounded-full"
          >
            😊 Un petit kiff
          </Button>
          {activeFilter && (
            <Button
              data-testid="filter-all"
              onClick={() => { setFilteredWishes(wishes); setActiveFilter(null); }}
              variant="ghost"
              className="rounded-full"
            >
              Tous
            </Button>
          )}
        </div>

        {filteredWishes.length === 0 ? (
          <Card className="shadow-card" data-testid="empty-wishlist">
            <CardContent className="py-12 text-center">
              <Gift className="w-16 h-16 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="font-heading text-xl font-bold mb-2">Aucun souhait pour le moment</h3>
              <p className="text-muted-foreground">{activeFilter ? "Aucun souhait dans cette catégorie." : "Cette liste est vide."}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredWishes.map((wish) => {
              const isReservedByMe = isAuthenticated && wish.reserved_by === currentUser?.user_id;
              const isReservedByOther = wish.is_reserved && !isReservedByMe;
              const isAlreadyReceived = wish.status === "received";
              const CategoryIcon = getCategoryIcon(wish.category);
              const hasImage = wish.og_image;
              
              return (
                <Card 
                  key={wish.wish_id} 
                  className={`shadow-card hover:shadow-float transition-all duration-300 relative overflow-hidden ${
                    isReservedByOther || isAlreadyReceived ? 'opacity-60' : ''
                  }`}
                  data-testid={`public-wish-${wish.wish_id}`}
                >
                  {isAlreadyReceived && (
                    <div className="absolute top-0 left-0 right-0 bg-red-500 text-white py-2 px-4 text-center font-bold z-10 animate-pulse">
                      🛍️ Il a déjà craqué 😅
                    </div>
                  )}
                  
                  {!isAlreadyReceived && isReservedByOther && (
                    <div className="absolute top-2 right-2 bg-destructive text-destructive-foreground px-3 py-1 rounded-full text-xs font-bold z-10">
                      Déjà réservé
                    </div>
                  )}
                  
                  {!isAlreadyReceived && isReservedByMe && (
                    <div className="absolute top-2 right-2 bg-secondary text-secondary-foreground px-3 py-1 rounded-full text-xs font-bold z-10">
                      Réservé par vous
                    </div>
                  )}
                  
                  {hasImage ? (
                    <div className="relative h-48 w-full overflow-hidden bg-muted">
                      <img 
                        src={wish.og_image} 
                        alt={wish.item_name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const fallback = e.target.parentElement.querySelector('.gift-fallback');
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                      <div className="gift-fallback hidden w-full h-full items-center justify-center bg-gradient-to-br from-primary/10 to-primary/5">
                        <Gift className="w-20 h-20 text-primary" strokeWidth={1.5} />
                      </div>
                      {wish.priority && PRIORITY_LABELS[wish.priority] && (
                        <span className={`absolute bottom-2 right-2 px-3 py-1 rounded-full text-xs font-bold ${PRIORITY_LABELS[wish.priority].color}`}>
                          {PRIORITY_LABELS[wish.priority].label}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="relative h-48 w-full overflow-hidden bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
                      <Gift className="w-20 h-20 text-primary" strokeWidth={1.5} />
                      {wish.priority && PRIORITY_LABELS[wish.priority] && (
                        <span className={`absolute bottom-2 right-2 px-3 py-1 rounded-full text-xs font-bold ${PRIORITY_LABELS[wish.priority].color}`}>
                          {PRIORITY_LABELS[wish.priority].label}
                        </span>
                      )}
                    </div>
                  )}
                  
                  <CardHeader>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <CardTitle className="font-heading text-lg" data-testid={`wish-name-${wish.wish_id}`}>
                          {wish.item_name}
                        </CardTitle>
                        {wish.og_price && (
                          <p className="text-sm text-primary font-bold mt-1">
                            {wish.og_price.includes('€') ? wish.og_price : `${wish.og_price} €`}
                          </p>
                        )}
                        <p className="text-sm text-muted-foreground mt-1">{wish.category}</p>
                        {wish.size && (
                          <p className="text-sm text-muted-foreground">Taille: {wish.size}</p>
                        )}
                        {wish.link && (
                          <a 
                            href={wish.link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-2"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Voir l'article
                          </a>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {renderReservationButton(wish, isAlreadyReceived, isReservedByMe, isReservedByOther)}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );

  function renderReservationButton(wish, isAlreadyReceived, isReservedByMe, isReservedByOther) {
    if (isAlreadyReceived) {
      return (
        <Button
          disabled
          variant="outline"
          className="w-full rounded-full font-bold text-red-500 border-red-200"
        >
          🛍️ Déjà acheté par lui-même
        </Button>
      );
    }
    
    if (isReservedByMe) {
      return (
        <Button
          data-testid={`unreserve-btn-${wish.wish_id}`}
          onClick={() => handleUnreserve(wish.wish_id)}
          variant="outline"
          className="w-full rounded-full font-bold"
        >
          Annuler la réservation
        </Button>
      );
    }
    
    if (isReservedByOther) {
      return (
        <Button
          disabled
          className="w-full rounded-full font-bold"
        >
          Non disponible
        </Button>
      );
    }
    
    if (!isAuthenticated) {
      return (
        <Button
          data-testid={`login-to-reserve-btn-${wish.wish_id}`}
          onClick={() => navigate(`/login?redirect=/wishlist/${username}`)}
          className="w-full rounded-full font-bold bg-primary hover:bg-primary/90"
        >
          <LogIn className="w-4 h-4 mr-2" />
          Se connecter pour offrir
        </Button>
      );
    }
    
    return (
      <Button
        data-testid={`reserve-btn-${wish.wish_id}`}
        onClick={() => handleReserve(wish.wish_id)}
        className="w-full rounded-full font-bold bg-primary hover:bg-primary/90"
      >
        <Heart className="w-4 h-4 mr-2" />
        Offrir ce cadeau
      </Button>
    );
  }
}
